/**
 * The tab nav row — [Stream · Challenges · Clips · Social], the live source
 * picker, and whatever the page hangs on the right.
 *
 * Presentational. Split out of `CreatorSection` for the 400-line ceiling; it is
 * markup with no decisions of its own beyond hiding the source picker when
 * there is nothing to switch between.
 */
import React from "react";
import { SourceIcons } from "@/widgets/SourceIcons";
import type { FeaturedStreamResponse } from "@/hooks/hub/useGetHub";
import type { ContentTab, StreamingSource } from "../shared";

export interface CreatorTabsRowProps {
    tab: ContentTab;
    onTab: (tab: ContentTab) => void;
    hasChallenges?: boolean;
    /** Source picker only appears while live — see below. */
    isLive: boolean;
    availablePlatforms: FeaturedStreamResponse[];
    activePlatform: StreamingSource | null;
    onSelectSource: (platform: StreamingSource) => void;
    liveStatusFetchedAt?: number;
    tabsActionsSlot?: React.ReactNode;
}

export const CreatorTabsRow: React.FC<CreatorTabsRowProps> = ({
    tab, onTab, hasChallenges, isLive, availablePlatforms, activePlatform,
    onSelectSource, liveStatusFetchedAt, tabsActionsSlot,
}) =>
    <div className="hcs-tabs-row">
        <div className="hcs-tabs" role="tablist" aria-label="Section">
            <button type="button" role="tab" aria-selected={tab === "STREAM"}
                className={`hcs-tabs__tab${tab === "STREAM" ? " is-active" : ""}`}
                onClick={() => onTab("STREAM")}>Stream</button>
            {hasChallenges &&
                <button type="button" role="tab" aria-selected={tab === "CHALLENGES"}
                    className={`hcs-tabs__tab${tab === "CHALLENGES" ? " is-active" : ""}`}
                    onClick={() => onTab("CHALLENGES")}>Challenges</button>
            }
            <button type="button" role="tab" aria-selected={tab === "CLIPS"}
                className={`hcs-tabs__tab${tab === "CLIPS" ? " is-active" : ""}`}
                onClick={() => onTab("CLIPS")}>Clips</button>
            <button type="button" role="tab" aria-selected={tab === "SOCIAL"}
                className={`hcs-tabs__tab${tab === "SOCIAL" ? " is-active" : ""}`}
                onClick={() => onTab("SOCIAL")}>Social</button>
        </div>
        {/*
          * Source picker is a "switch between LIVE streams" control,
          * not a "switch between historical content" control. Hide
          * entirely when no platform is live — there's nothing to
          * switch BETWEEN, and the offline hero is sourced from the
          * most-recent VOD across all platforms (no per-source
          * historical filter).
          */}
        {isLive && availablePlatforms.length > 1 &&
            <SourceIcons
                available={availablePlatforms}
                activePlatform={activePlatform}
                onSelect={onSelectSource}
                liveStatusFetchedAt={liveStatusFetchedAt}
            />
        }
        {tabsActionsSlot &&
            <div className="hcs-tabs-actions">{tabsActionsSlot}</div>
        }
    </div>;

